/** Generate Text result export — UTF-8 Markdown download (extensible). */

export type GeneratedTextExportFormat = 'markdown'

export interface ExportGeneratedTextArgs {
  text: string
  format?: GeneratedTextExportFormat
  title?: string | null
  /** Optional override timestamp for deterministic tests. */
  now?: Date
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function stamp(d: Date): string {
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}-${pad2(d.getHours())}${pad2(d.getMinutes())}`
}

/** Keep Unicode letters/numbers; collapse other runs to single hyphen. */
export function sanitizeGeneratedTextFilenameBase(raw: string | null | undefined): string {
  const s = String(raw || '')
    .normalize('NFKC')
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^\.+|\.+$/g, '')
  if (!s) return 'vistoera-generated-text'
  // Cap grapheme-ish length without cutting mid-surrogate (Array.from).
  const chars = Array.from(s)
  return chars.length > 80 ? chars.slice(0, 80).join('') : s
}

export function buildGeneratedTextDownloadFilename(args: {
  title?: string | null
  now?: Date
}): string {
  const base = sanitizeGeneratedTextFilenameBase(args.title) || 'vistoera-generated-text'
  return `${base}-${stamp(args.now || new Date())}.md`
}

/**
 * Download current generated text as UTF-8 Markdown.
 * Does not strip emoji / CJK. Future: txt / docx / pdf via same entry.
 */
export function exportGeneratedText(args: ExportGeneratedTextArgs): void {
  const text = typeof args.text === 'string' ? args.text : ''
  if (!text) return
  const format = args.format || 'markdown'
  if (format !== 'markdown') {
    throw new Error(`Unsupported export format: ${format}`)
  }
  // UTF-8 Blob — no BOM unless we later prove Windows Notepad needs it.
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = buildGeneratedTextDownloadFilename({ title: args.title, now: args.now })
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
